import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Grid } from '@mui/material';
import { BarStack, Bar } from '@visx/shape';
import { Group } from '@visx/group';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import PropTypes from 'prop-types';
import _ from 'lodash';

import {
  getParams,
  getMonths,
  thousandFormat,
  thousandFormat2,
  convertMonth,
  getMonths2,
  financialMonth,
} from '../../../../Utils/Functions';
import {
  enMonths,
  positiveActiveColor,
  positiveDisableColor,
  negativeActiveColor,
  negativeDisableColor,
  minusActiveColor,
  minusDisableColor,
  activeLabelColor,
} from '../../../../Assets/js/constant';

const x = (d) => d.month;
let tooltipTimeout;

const TopChart = ({
  summaryData,
  selectedYears,
  selectedMonths,
  selectedTopItems,
  defaultStartMonth,
  defaultMonth,
  handleFilter,
}) => {
  const canvasRef = useRef(null);
  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, showTooltip, hideTooltip } = useTooltip();

  const [resize, setResize] = useState(false);
  const [hoverBar, setHoverBar] = useState(null);
  const [selectedBars, setSelectedBars] = useState([]);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState([]);
  const [income, setIncome] = useState([]);
  const [forecasts, setForecasts] = useState([]);
  const [colors, setColors] = useState([]);
  const [xTransform, setXTransform] = useState(0);
  const [canvasIndex, setCanvasIndex] = useState(-1);
  const [dragFlag, setDragFlag] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [rectState, setRectState] = useState({
    X: 0, Y: 0, iX: 0, iY: 0, rX: 0, rY: 0, rW: 0, rH: 0
  });

  // Prepare data
  const prepareData = useCallback(() => {
    let dictData = {};
    let dictTotals = {};
    let dictForecasts = {};
    let dictYeartodate = [];
    let dictIncome = [];
    let yeartodate = [];
    let _data = [];
    let _totals = [];
    let _forecasts = [];
    let _income = [];
    let _colors = selectedYears.reduce((ret, year) => {
      ret.push(positiveActiveColor);
      return ret;
    }, []);
    let fiscalMonths = getMonths(defaultStartMonth);
    let fiscalMonths2 = getMonths2(defaultMonth, defaultStartMonth);
    fiscalMonths.forEach((month) => {
      dictData[month] = {};
      dictData[month]['month'] = month;
      dictData[month]['value'] = {};
      dictData[month]['yeartodate'] = 0;
      dictIncome[month] = {};
      dictIncome[month]['total'] = 0;
      dictIncome[month]['income'] = 0;
      dictIncome[month]['profit'] = 0;
      dictYeartodate[month] = 0;
      selectedYears.forEach((year) => {
        dictData[month][year] = 0;
      });
      dictTotals[month] = 0;
      dictForecasts[month] = 0;
      yeartodate[month] = 0;
    });
    let prevalue = 0, firstnode = 1;
    summaryData.forEach((item) => {
      let month = convertMonth(item.MonthName);
      if (dictData[month] !== undefined) {
        if (fiscalMonths2.indexOf(month) > -1) {
          dictData[month][selectedYears[0]] = parseFloat(item.Profit);
          dictData[month]['value'][selectedYears[0]] = item;
          dictTotals[month] += parseFloat(item.Profit);
          dictForecasts[month] += parseFloat(item.ForecastProfit);
          dictIncome[month]['income'] += parseFloat(item.Income);
          dictIncome[month]['profit'] += parseFloat(item.Profit);
          if (firstnode === 1) {
            dictData[month]['yeartodate'] = parseFloat(item.Profit);
            prevalue = parseFloat(item.Profit);
            firstnode = 0;
          } else {
            dictData[month]['yeartodate'] = prevalue + parseFloat(item.Profit);
            prevalue = dictData[month]['yeartodate'];
          }
        }
      }
    });
    fiscalMonths.forEach((month) => {
      _data.push(dictData[month]);
      _totals.push(dictTotals[month]);
      _forecasts.push(dictForecasts[month]);
      _income.push(dictIncome[month]);
    });
    let _xTransform = 0;
    let maxYValue = Math.max(
      Math.abs(Math.min(Math.min(..._totals), Math.min(..._forecasts))),
      Math.max(Math.max(..._totals), Math.max(..._forecasts)),
    );
    if (
      !isFinite(maxYValue) ||
      maxYValue === Number.NEGATIVE_INFINITY ||
      isNaN(maxYValue) ||
      maxYValue === 0
    )
      _xTransform = 30;
    else {
      if (parseInt(thousandFormat(maxYValue)[0]) > 7)
        _xTransform = 8.2 * (thousandFormat2(maxYValue).length + 1) + 8;
      else _xTransform = 8.2 * thousandFormat2(maxYValue).length + 8;
    }
    setData(_data);
    setTotals(_totals);
    setForecasts(_forecasts);
    setColors(_colors);
    setIncome(_income);
    setXTransform(_xTransform);
  }, [summaryData, selectedYears, defaultStartMonth, defaultMonth]);

  // Resize handler
  const onResize = useCallback(() => setResize((r) => !r), []);

  useEffect(() => {
    prepareData();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [prepareData, onResize]);

  useEffect(() => {
    prepareData();
  }, [summaryData, selectedTopItems, prepareData]);

  // Canvas drawing and selection logic
  const getMousePositionOnCanvas = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const onMouseMove = (event) => {
    if (!dragFlag || !canvasRef.current) return;

    const pos = getMousePositionOnCanvas(event);

    setRectState((prev) => ({
      ...prev,
      X: pos.x,
      Y: pos.y,
      rX: Math.min(prev.iX, pos.x),
      rY: Math.min(prev.iY, pos.y),
      rW: prev.iX ? Math.abs(prev.iX - pos.x) : 0,
      rH: prev.iY ? Math.abs(prev.iY - pos.y) : 0,
    }));

    const context = canvasRef.current.getContext('2d');
    context.clearRect(
      0,
      0,
      canvasRef.current.width,
      canvasRef.current.height,
    );
    drawRectangle();
  };

  const onMouseOut = () => {
    // Optionally handle mouse out
  };

  const endDraw = () => {
    if (rectState.iX && rectState.iY) {
      drawRectangle(true);
    }
  };

  const drawRectangle = (end = false) => {
    const context = canvasRef.current.getContext('2d');
    context.beginPath();
    context.lineWidth = '1';
    context.strokeStyle = '#00f3ff';
    context.rect(rectState.rX, rectState.rY, rectState.rW, rectState.rH);
    context.stroke();
    context.globalAlpha = 0.2;
    context.fillStyle = 'lightblue';
    context.fill();
    if (end) {
      drawSelectBar();
    }
  };

  const resetCanvas = () => {
    setRectState({
      X: 0,
      Y: 0,
      iX: 0,
      iY: 0,
      rX: 0,
      rY: 0,
      rW: 0,
      rH: 0,
    });
    setCanvasIndex(-1);
    setDragFlag(false);
    setIsMouseDown(false);
    const context = canvasRef.current.getContext('2d');
    context.clearRect(
      0,
      0,
      canvasRef.current.width,
      canvasRef.current.height,
    );
    context.beginPath();
  };

  const onSvgMouseDown = (event) => {
    if (event.button !== 0 || !canvasRef.current) return;

    const target = event.target;
    if (
      target &&
      (target.tagName === 'text' ||
        target.getAttribute('data-no-drag') === 'true')
    ) {
      return;
    }

    setIsMouseDown(true);

    if (dragFlag) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const startX = event.clientX - rect.left;
    const startY = event.clientY - rect.top;

    setCanvasIndex(0);
    setRectState({
      X: startX,
      Y: startY,
      iX: startX,
      iY: startY,
      rX: startX,
      rY: startY,
      rW: 0,
      rH: 0,
    });
    setDragFlag(true);
  };

  const onSvgMouseMove = (event) => {
    if (!isMouseDown || dragFlag || event.buttons !== 1 || !canvasRef.current)
      return;

    const rect = canvasRef.current.getBoundingClientRect();
    const startX = event.clientX - rect.left;
    const startY = event.clientY - rect.top;

    setCanvasIndex(0);
    setRectState({
      X: startX,
      Y: startY,
      iX: startX,
      iY: startY,
      rX: startX,
      rY: startY,
      rW: 0,
      rH: 0,
    });
    setDragFlag(true);
  };

  const onSvgMouseUp = () => {
    setIsMouseDown(false);
  };

  const onSvgMouseLeave = () => {
    setIsMouseDown(false);
  };

  const drawSelectBar = () => {
    const { X, iX, rX, rY } = rectState;
    let unitWidth = (window.innerWidth - 16 - xTransform) / 12;
    let startPosition = X >= iX ? iX - xTransform : X - xTransform;
    let endPosition = X >= iX ? X - xTransform : iX - xTransform;
    let startMonth = Math.floor(startPosition / unitWidth);
    let endMonth = Math.floor(endPosition / unitWidth);
    let _selectedTopItems = [],
      _selectedMonths = [];
    for (let i = startMonth; i <= endMonth; i++) {
      let _month = financialMonth(i, defaultStartMonth);
      _month = _month === 12 ? 0 : _month;
      if (i + 1 < defaultStartMonth)
        _selectedTopItems.push({ month: _month, year: selectedYears[0] - 1 });
      else _selectedTopItems.push({ month: _month, year: selectedYears[0] });
      _selectedMonths.push(_month);
    }

    // compute selectedYearMonth like handleLabel/handleBar
    let _selectedYearMonth;
    if (_selectedMonths.length > 0) {
      const selMonth = _selectedMonths[_selectedMonths.length - 1];
      let _selyear;
      let _monthCalc = selMonth + 1;
      if (_monthCalc >= defaultStartMonth) _selyear = selectedYears[0] - 1;
      else _selyear = selectedYears[0];
      _selectedYearMonth =
        _selyear +
        (_monthCalc > 9
          ? _monthCalc.toString()
          : '0' + _monthCalc.toString()) +
        '01';
    }

    if (rX === 0 && rY === 0 && _selectedTopItems.length > 0) {
      if (
        _.isEqual(selectedTopItems[0], _selectedTopItems[_selectedTopItems.length - 1])
      ) {
        if (selectedTopItems.length === _selectedTopItems.length)
          deSelectAll();
        else
          handleFilter({
            selectedTopItems: [_selectedTopItems[_selectedTopItems.length - 1]],
            selectedMonths: [_selectedMonths[_selectedTopItems.length - 1]],
            selectedYearMonth: _selectedYearMonth,
          });
      } else
        handleFilter({
          selectedTopItems: [_selectedTopItems[_selectedTopItems.length - 1]],
          selectedMonths: [_selectedMonths[_selectedTopItems.length - 1]],
          selectedYearMonth: _selectedYearMonth,
        });
    } else {
      if (
        _.isEqual(selectedTopItems[0], _selectedTopItems[_selectedTopItems.length - 1])
      ) {
        if (selectedTopItems.length === _selectedTopItems.length)
          deSelectAll();
        else
          handleFilter({
            selectedTopItems: _selectedTopItems,
            selectedMonths: _selectedMonths,
            selectedYearMonth: _selectedYearMonth,
          });
      } else
        handleFilter({
          selectedTopItems: _selectedTopItems,
          selectedMonths: _selectedMonths,
          selectedYearMonth: _selectedYearMonth,
        });
    }
    resetCanvas();
  };

  const deSelectAll = () => {
    if (hoverBar) hoverBar.classList.remove('barHover');
    selectedBars.forEach((selectedBar) => {
      selectedBar.classList.remove('barActive');
    });
    setHoverBar(null);
    setSelectedBars([]);
    localStorage.removeItem('fpyTopItems');
    handleFilter({
      selectedMonths: [],
      selectedTopItems: [],
    });
  };

  const getColor = (bar) => {
    const year = bar.key;
    const month = bar.bar.data.month;
    const actual = bar.bar.data.value[year]
      ? parseFloat(bar.bar.data.value[year].Profit)
      : 0;
    const forecast = bar.bar.data.value[year]
      ? parseFloat(bar.bar.data.value[year].ForecastProfit)
      : 0;
    let activeColor = actual < forecast ? negativeActiveColor : positiveActiveColor;
    let disableColor = actual < forecast ? negativeDisableColor : positiveDisableColor;
    if (actual < 0) {
      activeColor = minusActiveColor;
      disableColor = minusDisableColor;
    }
    if (selectedTopItems?.length === 0 && selectedMonths?.length === 0) {
      return activeColor;
    }
    if (selectedTopItems.length === 0)
      if (selectedMonths.indexOf(month) > -1) return activeColor;
    for (let i = 0; i < selectedTopItems.length; i++) {
      if (
        selectedTopItems[i].month === month &&
        selectedTopItems[i].year === year
      ) {
        return activeColor;
      }
    }
    return disableColor;
  };

  const handleBar = (event, bar) => {
    if (dragFlag) return;

    let _selectedBars, _selectedMonths, _selectedTopItems;
    const month = bar.bar.data.month;
    const year = bar.key;
    if (!event.shiftKey) {
      _selectedMonths = [];
      let exist = false;
      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
        if (selectedBar === event.target) exist = true;
      });
      if (exist && selectedBars.length === 1) {
        _selectedTopItems = [];
        _selectedMonths = [];
        _selectedBars = [];
      } else {
        _selectedTopItems = [{ month: month, year: year }];
        _selectedMonths = [month];
        _selectedBars = [event.target];
        event.target.classList.add('barActive');
      }
    }
    let _selectedYearMonth;
    let _selyear;
    let _month = bar.bar.data.month + 1;
    if (_month >= defaultStartMonth) _selyear = selectedYears[0] - 1;
    else _selyear = selectedYears[0];
    _selectedYearMonth =
      _selyear +
      (_month > 9 ? _month.toString() : '0' + _month.toString()) +
      '01';
    setSelectedBars(_selectedBars);
    handleFilter({
      selectedTopItems: _selectedTopItems,
      selectedMonths: _selectedMonths,
      selectedYearMonth: _selectedYearMonth,
    });
  };

  const handleLabel = (event, month) => {
    let _selectedMonths, _selectedTopItems, _selectedBars;
    let index = NaN;
    for (let i = 0; i < selectedMonths.length; i++) {
      if (selectedMonths[i] === month) {
        index = i;
        break;
      }
    }
    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedMonths = selectedMonths.slice();
      _selectedTopItems = selectedTopItems.slice();
      if (isNaN(index)) {
        _selectedMonths.push(month);
        _selectedTopItems.push({ year: selectedYears[0], month: month });
      } else {
        _selectedMonths.splice(index, 1);
        _selectedTopItems.splice(index, 1);
      }
    } else {
      _selectedBars = [];
      _selectedTopItems = [];
      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
      });
      if (!isNaN(index) && selectedMonths.length === 1) {
        _selectedMonths = [];
        _selectedTopItems = [];
      } else {
        _selectedMonths = [month];
        _selectedTopItems = [{ year: selectedYears[0], month: month }];
      }
    }
    setSelectedBars(_selectedBars);
    let _selectedYearMonth;
    let _selyear;
    let _month = month + 1;
    if (_month >= defaultStartMonth) _selyear = selectedYears[0] - 1;
    else _selyear = selectedYears[0];
    _selectedYearMonth =
      _selyear +
      (_month > 9 ? _month.toString() : '0' + _month.toString()) +
      '01';
    handleFilter({
      selectedMonths: _selectedMonths,
      selectedTopItems: _selectedTopItems,
      selectedYearMonth: _selectedYearMonth,
    });
  };

  const showTooltipHandler = (event, xScale, bar, isBar = true) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);
    let tooltipdata = {}, top, left;
    if (isBar) {
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setHoverBar(event.target);
      top = event.clientY - 100;
      left =
        window.innerWidth / 2 < event.nativeEvent.layerX
          ? event.nativeEvent.layerX - 190
          : event.nativeEvent.layerX + 40;
      tooltipdata = bar;
    } else {
      top = event.clientY - 100;
      left =
        window.innerWidth / 2 < event.nativeEvent.layerX
          ? event.nativeEvent.layerX - 190
          : event.nativeEvent.layerX + 40;
      tooltipdata['forecast'] = bar;
    }
    tooltipdata['isBar'] = isBar;
    // calculate mtdSum
    let selSum = 0;
    let mtdMonths = 0;
    if (data && selectedTopItems.length > 1)
      data.forEach((item) => {
        selectedTopItems.forEach((subitem) => {
          if (item.month === subitem.month) {
            selSum += parseFloat(item[selectedYears[0]]);
            mtdMonths++;
          }
        });
      });
    tooltipdata['selSum'] = selSum;
    tooltipdata['items'] = mtdMonths;
    showTooltip && showTooltip({
      tooltipData: tooltipdata,
      tooltipTop: top,
      tooltipLeft: left,
    });
  };

  const hideTooltipHandler = () => {
    tooltipTimeout = setTimeout(() => {
      if (hoverBar) hoverBar.classList.remove('barHover');
      setHoverBar(null);
      hideTooltip && hideTooltip();
    }, 300);
  };

  // --- Render ---
  const width = window.innerWidth - 16;
  const height = 174;
  const margin = {
    top: 20,
    right: 0,
    bottom: 20,
    left: xTransform,
  };
  const { pageYOffset, xMax, yMax } = getParams(
    window,
    width,
    height,
    margin,
  );
  const offsetX = 3.5;
  const offsetWidth = -1.2;
  let minY = Math.min(Math.min(...totals), Math.min(...forecasts));
  minY = Math.min(0, minY);
  let maxY = Math.max(Math.max(...totals), Math.max(...forecasts));
  maxY = Math.max(0, maxY);
  // scales
  const xScale = scaleBand()
    .domain(data.map(x))
    .padding(0.2)
    .rangeRound([0, xMax]);
  const yScale = scaleLinear({
    domain:
      maxY / 3 > Math.abs(minY)
        ? minY > 0
          ? [0, maxY * 1.3]
          : [-maxY / 3, maxY * 1.3]
        : minY > 0
        ? [0, maxY * 1.3]
        : [minY * 1.2, maxY * 1.3],
    range: [yMax, 0],
    nice: true,
  });
  const color = scaleOrdinal({
    domain: selectedYears,
    range: colors,
  });
  let fiscalMonths = getMonths(defaultStartMonth);

  return (
    <div>
      <div className="subtitle mb-10">Actual vs Forecast Profit</div>
      <Grid container>
        <canvas
          width={width}
          height={height - 20}
          ref={canvasRef}
          onMouseMove={onMouseMove}
          onMouseUp={endDraw}
          onMouseOut={onMouseOut}
          style={{ position: 'fixed', zIndex: canvasIndex }}
        />
        <svg
          width={width}
          height={height}
          onMouseDown={onSvgMouseDown}
          onMouseMove={onSvgMouseMove}
          onMouseUp={onSvgMouseUp}
          onMouseLeave={onSvgMouseLeave}
        >
          <rect
            x={0}
            y={0}
            width={width}
            height={height}
            fill="transparent"
          />
          <Group top={margin.top} left={margin.left}>
            <BarStack
              data={data}
              keys={selectedYears}
              x={x}
              xScale={xScale}
              yScale={yScale}
              color={color}
            >
              {(barStacks) => {
                return barStacks.map((barStack) => {
                  return barStack.bars.map((bar) => {
                    return (
                      <rect
                        key={`bar-income-${barStack.index}-${bar.index}`}
                        x={bar.x}
                        y={
                          isNaN(bar.height)
                            ? 0
                            : bar.height <= 0
                            ? yScale(0)
                            : bar.y
                        }
                        height={
                          isNaN(bar.height)
                            ? 0
                            : bar.height < 0
                            ? bar.y - yScale(0)
                            : bar.height
                        }
                        width={bar.width}
                        fill={getColor(bar)}
                        onClick={(event) => handleBar(event, bar)}
                        onMouseLeave={hideTooltipHandler}
                        onMouseMove={(event) =>
                          showTooltipHandler(event, xScale, bar)
                        }
                        onTouchEnd={hideTooltipHandler}
                        onTouchMove={(event) =>
                          showTooltipHandler(event, xScale, bar)
                        }
                      />
                    );
                  });
                });
              }}
            </BarStack>
            {income.map((item, index) => {
              return (
                <text
                  key={`text-${index}`}
                  x={
                    xScale.paddingInner() * xScale.step() +
                    index * xScale.step() +
                    offsetX +
                    (xScale.step() -
                      (xScale.paddingInner() * xScale.step()) / 2) /
                      2 -
                    5
                  }
                  fontSize={11}
                  font="Verdana"
                  y={Math.max(
                    12 - margin.top,
                    Math.min(
                      yMax - 5,
                      Number.isFinite(yScale(item.profit))
                        ? (yScale(item.profit) > yScale(0)
                            ? yScale(0) - 7
                            : yScale(item.profit) - 7)
                        : 12,
                    ),
                  )}
                  textAnchor="middle"
                >
                  {item.income === 0
                    ? ''
                    : Math.round((item.profit / item.income) * 100) > 0
                    ? Math.round(
                        (item.profit / item.income) * 100,
                      ).toString() + '%'
                    : '('
                    + Math.abs(
                        Math.round((item.profit / item.income) * 100),
                      )
                    + '%)'}
                </text>
              );
            })}
            {forecasts.map((forecast, index) => {
              return (
                <Group key={`bar-forecast-${index}`}>
                  <Bar
                    width={
                      (xMax - xScale.paddingInner() * xScale.step()) /
                        fiscalMonths.length +
                      offsetWidth
                    }
                    height={1}
                    x={
                      (xScale.paddingInner() * xScale.step()) / 2 +
                      index * xScale.step() +
                      offsetX
                    }
                    y={isNaN(yScale(forecast)) ? 0 : yScale(forecast)}
                    fill="black"
                    stroke="black"
                    strokeWidth={1}
                    onMouseLeave={hideTooltipHandler}
                    onMouseMove={(event) =>
                      showTooltipHandler(event, xScale, forecast, false)
                    }
                    onTouchEnd={hideTooltipHandler}
                    onTouchMove={(event) =>
                      showTooltipHandler(event, xScale, forecast, false)
                    }
                  />
                </Group>
              );
            })}
            <Bar
              width={xMax}
              height={1}
              y={isNaN(yScale(0)) ? 0 : yScale(0)}
            />
            <AxisLeft
              numTicks={4}
              scale={yScale}
              stroke="black"
              tickStroke="black"
              tickLabelProps={(value, index) => ({
                fill: 'black',
                fontSize: 11,
                textAnchor: 'end',
                dy: '0.33em',
                dx: '-0.33em',
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text {...tickProps} fill="black">
                  ${formattedValue}
                </text>
              )}
            />
            {fiscalMonths.map((month, index) => {
              return (
                <rect
                  key={index}
                  x={
                    (xScale.paddingInner() * xScale.step()) / 2 +
                    index * xScale.step() +
                    offsetX
                  }
                  y={yMax ? yMax : 0}
                  width={
                    (xMax - (xScale.paddingInner() * xScale.step()) / 2) /
                      fiscalMonths.length +
                    offsetWidth
                  }
                  height={margin.bottom}
                  fill="transparent"
                  onClick={(event) => handleLabel(event, month)}
                />
              );
            })}
            <AxisBottom
              hideTicks={true}
              scale={xScale}
              top={yMax}
              stroke="black"
              tickStroke="black"
              tickLabelProps={(value, index) => ({
                fontSize: 11,
                textAnchor: 'middle',
                dy: '-0.5em',
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text
                  {...tickProps}
                  style={{
                    fontWeight:
                      selectedMonths.indexOf(parseInt(formattedValue)) > -1
                        ? 'bold'
                        : 500,
                  }}
                  fill={
                    selectedMonths.indexOf(parseInt(formattedValue)) > -1
                      ? activeLabelColor
                      : 'black'
                  }
                  onClick={(event) =>
                    handleLabel(event, parseInt(formattedValue))
                  }
                >
                  {enMonths[formattedValue]}
                </text>
              )}
            />
          </Group>
        </svg>
        {tooltipOpen && (
          <TooltipWithBounds
           
            top={tooltipTop + pageYOffset}
            left={tooltipLeft}
          >
            {tooltipData.isBar ? (
              <div className="color-black" style={{ padding: 0, margin: 0 }}>
                {tooltipData.selSum !== 0 ? (
                  <div
                    className="ft-12"
                    style={{
                      backgroundColor: '#dddddd',
                      margin: 0,
                      minHeight: 25,
                    }}
                  >
                    <div style={{ padding: 5 }}>
                      {tooltipData.items} items selected SUM(ProfitMTD
                      HistoricalOnly):&nbsp;
                      <strong>{thousandFormat2(tooltipData.selSum)}</strong>
                    </div>
                  </div>
                ) : (
                  ''
                )}
                <div style={{ margin: 5 }}>
                  <div className="pdv-10">
                    <strong>
                      FY {tooltipData.key} :{' '}
                      {enMonths[x(tooltipData.bar.data)]}
                    </strong>
                  </div>
                  <div className="ft-12">
                    Actual:{' '}
                    <strong>
                      {thousandFormat2(
                        tooltipData.bar.data.value[tooltipData.key].Profit,
                      )}
                    </strong>
                  </div>
                  <div className="ft-12">
                    Forecast:{' '}
                    <strong>
                      {thousandFormat2(
                        tooltipData.bar.data.value[tooltipData.key]
                          .ForecastProfit,
                      )}
                    </strong>
                  </div>
                  <br />
                  <div className="ft-12">
                    Year to Date:{' '}
                    <strong>
                      {thousandFormat2(tooltipData.bar.data['yeartodate'])}
                    </strong>
                  </div>
                </div>
              </div>
            ) : (
              <div className="color-black" style={{ margin: 5 }}>
                Sum ProfitMTDForecast HistoricalOnly ={' '}
                {thousandFormat2(tooltipData.forecast)}
              </div>
            )}
          </TooltipWithBounds>
        )}
      </Grid>
    </div>
  );
};

TopChart.propTypes = {
  summaryData: PropTypes.array.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedMonths: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  handleFilter: PropTypes.func.isRequired,
};

export default TopChart;
